import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, Sparkles, RotateCcw } from 'lucide-react';
import Markdown from './Markdown';
import { useChat } from './useChat';

const SUGGESTIONS = [
  'I feel anxious — which meditation helps?',
  'Recommend a morning meditation',
  'What upcoming retreats do you have?',
  'How do memberships work?',
];

const WELCOME = "Namaste 🙏 I'm your Seeker's Guide. Ask me about meditation, Osho's vision, or anything on Osho Universal — retreats, memberships, techniques or resources. How can I support you today?";

const ChatPanel = ({ variant = 'widget' }) => {
  const { messages, loading, send, reset } = useChat({ loadHistory: variant === 'page' });
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);
  const isPage = variant === 'page';

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const submit = (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    send(input);
    setInput('');
  };

  const empty = messages.length === 0;

  return (
    <div className={`flex flex-col ${isPage ? 'h-[70vh] rounded-3xl border border-rose/40 bg-white/70' : 'h-full'}`} data-testid="chat-panel" data-no-translate>
      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chat-messages">
        {empty && (
          <div className="space-y-4">
            <div className="flex gap-2.5">
              <div className="w-8 h-8 rounded-full bg-mahogany text-cream flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4" /></div>
              <div className="bg-rose-soft/50 rounded-2xl rounded-tl-sm px-4 py-3 text-burgundy/90 text-[15px] leading-relaxed">{WELCOME}</div>
            </div>
            <div className="flex flex-wrap gap-2 pl-11">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="chat-suggestion"
                  className="text-left text-sm px-3 py-2 rounded-full bg-white/70 border border-rose/50 text-burgundy hover:border-rose hover:bg-rose-soft/40 transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          m.role === 'user' ? (
            <div key={i} className="flex justify-end" data-testid="chat-msg-user">
              <div className="bg-mahogany text-cream rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[85%] text-[15px] leading-relaxed whitespace-pre-wrap">{m.content}</div>
            </div>
          ) : (
            <div key={i} className="flex gap-2.5" data-testid="chat-msg-assistant">
              <div className="w-8 h-8 rounded-full bg-mahogany text-cream flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4" /></div>
              <div className="bg-rose-soft/50 rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[85%] text-burgundy/90">
                {m.content ? <Markdown text={m.content} /> : <Loader2 className="w-4 h-4 animate-spin text-mahogany" />}
              </div>
            </div>
          )
        ))}
      </div>

      {/* disclaimer */}
      <div className="px-4 pt-1 text-[11px] text-mahogany/50 text-center">
        The Guide offers reflections, not medical advice. In a crisis, contact local emergency services.
      </div>

      {/* input */}
      <form onSubmit={submit} className="p-3 flex items-end gap-2 border-t border-rose/30" data-testid="chat-form">
        {/* honeypot (hidden from users, catches bots) */}
        <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          rows={1}
          maxLength={1200}
          placeholder="Ask the Seeker's Guide…"
          data-testid="chat-input"
          className="flex-1 resize-none bg-white/80 border border-rose/50 rounded-2xl px-4 py-2.5 text-burgundy focus:outline-none focus:border-mahogany max-h-32 text-[15px]"
        />
        {!empty && (
          <button type="button" onClick={reset} title="New chat" className="p-2.5 text-mahogany/60 hover:text-mahogany" data-testid="chat-reset">
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
        <button type="submit" disabled={loading || !input.trim()} data-testid="chat-send"
          className="w-11 h-11 shrink-0 rounded-full bg-mahogany text-cream flex items-center justify-center hover:bg-burgundy disabled:opacity-40 transition-colors">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
};

export default ChatPanel;
